// File: cgp-authentication.js
// Author: Brandon Shreve
// Date: 1/9/2017
// Desc: Authentication module that handles integrating with CGP Authentication.
//       Handles redirecting to GAME login page and reading the cookie that has our authorization token.
//       Has methods to get the authorization token as well as the logged in user's information.
// Other Contributors: Joseph M Diaz

import { config } from '../config/config';
import { cgpCookieHelper } from './cgp-cookie-helper';
import { cgpSecurityUrls, cgpLoginUrls } from '../../src/config/config.base';
import { getEnv } from './utility';
import { BehaviorSubject } from 'rxjs';
import { servicesUrl } from '../app/components/ve/ve.config';

export const USER_PROFILE: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
const UNAUTHORIZED_MESSAGE = `
You are not authorized to view this app. Please contact:\n
For Body: Annette Atherton-Throne (<a href="mailto:annette.c.atherton-throne@gm.com?
subject=Access to Body Virtual Engineering Work Order Application">annette.c.atherton-throne@gm.com</a>)\n
For GA: Pete Yang (<a href="mailto:pete.yang@gm.com?
subject=Access to GA Virtual Engineering Work Order Application">pete.yang@gm.com</a>)`;

const FAILED_USER_PROFILE_MESSAGE = `An error has occurred when trying to download your user profile. Please refresh and try again.`

function singleSignOnRedirect(redirectLink: any) {
    const cgpSecurityUrl = cgpLoginUrls[getEnv()] + encodeURIComponent(redirectLink);

    window.history.pushState({}, document.title, redirectLink);
    window.location.replace(cgpSecurityUrl);
}

function updateMescLoading(msg) {
    const loadingTextElement = document.getElementById('loading');
    const loadingSymbolElement = document.getElementById('loading-spinner');
    const unauthorizedElement = document.getElementById('unauthorized');

    if (msg === UNAUTHORIZED_MESSAGE) {
        loadingTextElement.style.display = 'none';
        unauthorizedElement.style.display = 'block';
        unauthorizedElement.lastElementChild.innerHTML = msg;
    } else {
        loadingTextElement.lastElementChild.innerHTML = msg;
    }
    loadingSymbolElement.style.display = 'none';
}

function validateToken(callback: Function) {
    const token = getAuthToken();
    const location = window.location.href.split('?')[0];

    if (token) {
        const url = cgpSecurityUrls[getEnv()] + 'validateToken';
        const http = new XMLHttpRequest();
        window.history.pushState({}, document.title, location);

        http.open('POST', url, true);
        http.setRequestHeader('Content-type', 'application/json');
        http.setRequestHeader('Authorization', token);

        http.onreadystatechange = function () {
            if (http.readyState !== XMLHttpRequest.DONE) {
                return;
            }

            if (http.status === 200) {
                const newToken = http.getResponseHeader(config.authentication.authenticationCookie);
                cgpCookieHelper.setAuthCookie(newToken.replace(/^\s*Bearer\s*/, ''));
                if (callback) { callback(); }
            } else {
                singleSignOnRedirect(location);
            }
        };
        http.send();
    } else {
        singleSignOnRedirect(location);
    }
}

function getUserProfile(callback: Function) {
    const token = getAuthToken();
    const location = window.location.href.split('?')[0];

    if (token) {
        const url = servicesUrl[getEnv()] + '/Common/Authorization/userprofile';
        const http = new XMLHttpRequest();
        window.history.pushState({}, document.title, location);

        http.open('GET', url, true);
        http.setRequestHeader('Content-type', 'application/json');
        http.setRequestHeader('Authorization', token);

        http.onreadystatechange = function () {
            if (http.readyState !== XMLHttpRequest.DONE) {
                return;
            }

            if (http.status === 200) {
                const response = JSON.parse(http.response);
                //if the user is found but doesn't have a role with Body or GA then we can assume the user is unauthorized 
                if (response.ModuleClaims.length === 0 || (response.BodyRoles.length === 0 && response.GARoles.length === 0)) {
                    updateMescLoading(UNAUTHORIZED_MESSAGE); 
                } else {
                    USER_PROFILE.next(response);
                    if (callback) { callback(); }
                }
            } else {
                updateMescLoading(FAILED_USER_PROFILE_MESSAGE);
            }
        };
        http.send();
    } else {
        singleSignOnRedirect(location);
    }
}

function refreshToken(callback: Function) {
    const token = getAuthToken();
    const url = cgpSecurityUrls[getEnv()] + 'refreshToken';
    const http = new XMLHttpRequest();

    http.open('POST', url, true);
    http.setRequestHeader('Content-type', 'application/json');
    http.setRequestHeader('Authorization', token);

    http.onreadystatechange = function () {
        if (http.readyState !== XMLHttpRequest.DONE) {
            return;
        }

        if (http.status === 200) {
            const newToken = http.getResponseHeader(config.authentication.authenticationCookie);
            cgpCookieHelper.setAuthCookie(newToken.replace(/^\s*Bearer\s*/, ''));
            if (callback) { callback(null, 'success'); }
        } else {
            if (callback) { callback('error', null); }
        }
    };
    http.send();
}

function invalidateToken(callback: Function) {
    cgpCookieHelper.deleteAuthCookie();
}

function parseToken(token) {
    if (token) {
        return token.replace('Bearer_', 'Bearer ');
    } else {
        return '';
    }
}

function getAuthToken() {
    const token = cgpCookieHelper.retrieveCookie(config.authentication.authenticationCookie);
    return parseToken(token);
}

function getUserInfo() {
    const userInfo = cgpCookieHelper.retrieveCookie(config.authentication.userInfoCookie);

    if (userInfo) {
        return JSON.parse(JSON.parse(userInfo));
    }

    return null;
}

export const cgpAuthentication = {
    singleSignOnRedirect,
    getAuthToken,
    getUserInfo,
    validateToken,
    invalidateToken,
    refreshToken,
    getUserProfile,
    USER_PROFILE,
    updateMescLoading,
};
